/**
 * page_bar_controller.js — the persistent page utility bar: find-in-page,
 * jump-to-section, and a page-scoped assistant, all through one input.
 *
 * States: collapsed, expanded, answering.
 *
 * ONE INPUT, TWO MODES, AND THE READER DECIDES WHICH. Typing filters the page
 * and costs nothing. Asking costs a model call, so it never happens on a
 * keystroke: it takes a deliberate submit, and the Ask row only offers itself
 * once the query has stopped looking like a lookup. Find results stay on
 * screen above the answer, so a poor answer still leaves the reader holding
 * the links they were one keypress away from.
 *
 * ZERO NETWORK ON KEYSTROKE. The index is every h2/h3 with an id and every
 * element carrying data-block, read out of the DOM once on connect. The page
 * already contains every string the reader can search for, so fetching them
 * again would be paying twice for what is on screen. It also means find works
 * with no connection at all, which is the situation half these readers are in.
 *
 * The index is per-page, so the bar is deliberately NOT data-turbo-permanent.
 * connect() runs on every Turbo visit and rebuilds against the new document; a
 * persisted bar would search the previous page.
 *
 * Nothing here reads layout during scroll. The scroll listener compares
 * window.scrollY against a number and nothing else: measuring an element inside
 * a scroll handler forces synchronous layout on the one interaction that has to
 * stay smooth.
 *
 * Enhancement, not a requirement. Every result target is a heading or block
 * that is already in the server-rendered page and already reachable by its own
 * anchor; the bar makes reaching it faster and nothing here gates content.
 */

import { Controller } from "@hotwired/stimulus";

const MAX_RESULTS = 8;
const SNIPPET_RADIUS = 45;
const COLLAPSE_AFTER_SCROLL = 120;
/** How far the panel must be dragged down before letting go dismisses it. */
const DRAG_DISMISS_PX = 60;
/**
 * How long a scroll gesture over the panel keeps the panel open for.
 *
 * overscroll-behavior stops a scrollable panel chaining to the page, but a
 * panel shorter than its content limit does not scroll at all, so the gesture
 * lands on the page and would dismiss the thing being read.
 */
const PANEL_SCROLL_GRACE = 250;
/**
 * How long after the panel opens, or the keyboard resizes the viewport, a page
 * scroll is the browser's and not the reader's.
 *
 * Tapping the input on a phone opens the software keyboard, and the browser
 * scrolls the page to keep the focused input in view. Counted as the reader
 * scrolling on, that closed the panel and blurred the input the moment it
 * opened, which shut the keyboard again: on a phone the bar could not be used.
 */
const KEYBOARD_GRACE = 800;
const HIGHLIGHT_MS = 2000;
const MAX_CITATIONS = 3;
const MAX_TURNS = 6;
const FIRST_TOKEN_MS = 20000;

/** A query that reads like a question rather than a lookup. */
const QUESTION_WORD = /^(who|what|how|why|can|does|do|is|are|should|when|where)\b/i;

/**
 * The DOM half of the citation contract.
 *
 * application/services/seo_page_context_service.py decides what the assistant
 * reads: _DROP_ELEMENTS, _DROP_BY_ATTR and _DROP_BY_CLASS strip chrome out of
 * <main>, and _ELEMENT_RE says what counts as a block in what is left. A
 * citation of the form [[t17]] means "the seventeenth of those blocks", so
 * these two selectors have to name exactly what those regexes name. Drift
 * makes every citation on a template page land one paragraph out, silently.
 * tests/test_page_bar_citations.py pins the two lists against each other.
 */
const CITATION_DROP = "script,style,svg,nav,footer,template,noscript,"
    + '[data-page-context="skip"],#public-chat-widget,#toast-root,'
    + ".toc-rail,.cta-band,.seo-related,.crumbs";
const CITATION_BLOCK = "p,ul,ol,table,blockquote,figure,aside";

const CHECK_LINE = /^\s*<check>\s*(page|facts|related|none)\s*<\/check>/i;
const CITATION = /\[\[([A-Za-z0-9][A-Za-z0-9_-]{0,63})\]\]/g;
/**
 * A page reference the assistant wrote as [[Title | /path]], mixing the
 * related_pages format with the [[anchor]] one. The navigation lives in a card
 * below the answer, so only the label survives here -- left alone it renders as
 * literal brackets and a raw path in the middle of a sentence.
 */
const PAGE_REFERENCE = /\[\[\s*([^\]|]+?)\s*\|\s*\/[^\s\]|]+\s*\]\]/g;

/** Text of an element with its whitespace collapsed. */
function textOf(element) {
    return (element.textContent || "").replace(/\s+/g, " ").trim();
}

function escapeHtml(text) {
    return text.replace(/[&<>"]/g, (character) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;",
    }[character]));
}

/**
 * A window of text around the match, with the match marked.
 *
 * Escaped first and marked second, so page text can never inject markup here:
 * the blog body is author-controlled but this is still the one place where page
 * content becomes innerHTML.
 */
function snippet(text, at, length) {
    const from = Math.max(0, at - SNIPPET_RADIUS);
    const to = Math.min(text.length, at + length + SNIPPET_RADIUS);
    const lead = from > 0 ? "…" : "";
    const tail = to < text.length ? "…" : "";
    return lead
        + escapeHtml(text.slice(from, at))
        + "<mark>" + escapeHtml(text.slice(at, at + length)) + "</mark>"
        + escapeHtml(text.slice(at + length, to))
        + tail;
}

/**
 * An href the bar is willing to render, reduced to a path, or null.
 *
 * The prompt forbids invented URLs and the answer is generated text, so this
 * is the second lock on the same door. Same-origin only: every page the model
 * is given is a page on this site, so anything pointing elsewhere is either a
 * hallucination or an injection from page content, and neither is a link the
 * reader should be offered. On a dev host the model's absolute chippieapp.com
 * links fail this and render as plain text, which is the right failure.
 */
function safeHref(href) {
    let url;
    try {
        url = new URL(href, window.location.href);
    } catch (_error) {
        return null;
    }
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
}

/** Bold and links inside one paragraph, over already-escaped text. */
function inlineMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, href) => {
            const safe = safeHref(href);
            return safe ? '<a href="' + safe + '">' + label + "</a>" : label;
        })
        // A page the assistant named arrives as a bare URL, because it is told
        // to copy the link exactly rather than dress it up as markdown. Without
        // this it renders as dead text the reader has to retype. Runs last and
        // skips a URL preceded by a quote or paren so it cannot re-enter an
        // href written by the rule above.
        .replace(/(^|[\s>])(https?:\/\/[^\s<>"')\]]+)/g, (match, lead, url) => {
            const safe = safeHref(url);
            return safe ? lead + '<a href="' + safe + '">' + url + "</a>" : match;
        });
}

/**
 * The small slice of markdown an answer actually uses: paragraphs, bullet
 * lists, bold and links. Deliberately not a markdown library: this runs on
 * model output, so every construct it understands is a construct someone can
 * try to smuggle markup through, and four is a surface I can read in full.
 */
function renderMarkdown(text) {
    return text
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block) => {
            const lines = block.split("\n");
            if (!lines.every((line) => /^\s*[-*]\s+/.test(line))) {
                return "<p>" + inlineMarkdown(block) + "</p>";
            }
            return "<ul>" + lines
                .map((line) => "<li>" + inlineMarkdown(line.replace(/^\s*[-*]\s+/, "")) + "</li>")
                .join("") + "</ul>";
        })
        .join("");
}

/**
 * How far to lift the bar to clear the software keyboard, or 0.
 *
 * Only a keyboard-sized gap counts. Scrolling up on an iPhone brings the
 * browser's own toolbars back, which also shrinks the visual viewport; lifting
 * the bar for that moved it against the browser's own repositioning on every
 * scroll frame, and the bar flickered. A toolbar is under 100px, a keyboard
 * well over 200.
 */
const KEYBOARD_MIN_PX = 150;

export function keyboardLift({ innerHeight, viewportHeight, viewportTop }) {
    const overlap = innerHeight - (viewportHeight + viewportTop);
    return overlap > KEYBOARD_MIN_PX ? overlap : 0;
}

/**
 * What a page scroll means while the panel is open.
 *
 * "keep" leaves the panel alone, "rebase" moves the scroll baseline to here
 * (the browser moved the page, not the reader), and "collapse" closes it.
 * Pure so tests/js/page_bar_scroll.test.mjs can hold it without a DOM.
 */
export function scrollOutcome({ now, scrollY, expandedAt, panelScrollAt, viewportAt }) {
    if (now - viewportAt < KEYBOARD_GRACE) return "rebase";
    if (now - panelScrollAt < PANEL_SCROLL_GRACE) return "keep";
    return Math.abs(scrollY - expandedAt) > COLLAPSE_AFTER_SCROLL ? "collapse" : "keep";
}

export default class extends Controller {
    static targets = [
        "input", "panel", "results", "ask", "starters",
        "dismiss", "past", "askedBy", "question", "answeredBy", "answer",
        "sources", "chips", "elsewhere", "cards", "after", "thanks", "fallback",
        "caution", "disclosure",
    ];
    static values = { path: String, askUrl: String };

    connect() {
        this.entries = this._buildIndex();
        this.blocks = this._buildBlockIndex();
        this.matches = [];
        this.cursor = -1;
        this.expanded = false;
        this.expandedAt = 0;
        this._resetConversation();

        this.dragFrom = null;
        this.panelScrollAt = 0;
        this.viewportAt = 0;
        this._onKey = (event) => this._globalKey(event);
        this._onPanelScroll = () => { this.panelScrollAt = Date.now(); };
        this._onScroll = () => this._maybeCollapse();
        this._onViewport = () => this._pinAboveKeyboard();
        // Resize, not scroll: the keyboard opening or closing resizes the
        // visual viewport, but the reader scrolling with it open only scrolls it.
        this._onViewportResize = () => { this.viewportAt = Date.now(); this._pinAboveKeyboard(); };
        this._onOutside = (event) => this._outsideClick(event);
        this._onDragStart = (event) => this._dragStart(event);
        this._onDragMove = (event) => this._dragMove(event);
        this._onDragEnd = (event) => this._dragEnd(event);
        document.addEventListener("keydown", this._onKey);
        document.addEventListener("scroll", this._onScroll, { passive: true });
        document.addEventListener("pointerdown", this._onOutside);
        this.dismissTarget.addEventListener("pointerdown", this._onDragStart);
        this.panelTarget.addEventListener("wheel", this._onPanelScroll, { passive: true });
        this.panelTarget.addEventListener("touchmove", this._onPanelScroll, { passive: true });
        document.addEventListener("pointermove", this._onDragMove, { passive: true });
        document.addEventListener("pointerup", this._onDragEnd);
        document.addEventListener("pointercancel", this._onDragEnd);

        // The software keyboard opens AFTER the input takes focus, so measuring
        // once in expand() measures a viewport the keyboard has not shrunk yet
        // and leaves the bar behind it. This fires as the keyboard animates.
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", this._onViewportResize);
            window.visualViewport.addEventListener("scroll", this._onViewport);
        }
    }

    disconnect() {
        document.removeEventListener("keydown", this._onKey);
        document.removeEventListener("scroll", this._onScroll);
        document.removeEventListener("pointerdown", this._onOutside);
        this.dismissTarget.removeEventListener("pointerdown", this._onDragStart);
        this.panelTarget.removeEventListener("wheel", this._onPanelScroll);
        this.panelTarget.removeEventListener("touchmove", this._onPanelScroll);
        document.removeEventListener("pointermove", this._onDragMove);
        document.removeEventListener("pointerup", this._onDragEnd);
        document.removeEventListener("pointercancel", this._onDragEnd);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", this._onViewportResize);
            window.visualViewport.removeEventListener("scroll", this._onViewport);
        }
        this._onKey = null;
        this._onScroll = null;
        this._onViewport = null;
        this._onOutside = null;
        this._onPanelScroll = null;
        this._onDragStart = null;
        this._onDragMove = null;
        this._onDragEnd = null;
        this.entries = null;
        this.blocks = null;
        this.matches = null;
        // A stream still arriving when the reader navigates away has nowhere
        // to render; abort it rather than let fetch hold the connection and
        // the closure hold the detached DOM.
        this._abort();
    }

    /**
     * Headings and blocks, in document order, from the page's own main content.
     *
     * A block's heading is whichever heading most recently preceded it, which
     * is what lets a passage result say where in the document it lives.
     */
    _buildIndex() {
        const nodes = this._main().querySelectorAll("h2[id], h3[id], [data-block]");
        const entries = [];
        let section = "";

        nodes.forEach((node) => {
            const tag = node.tagName.toLowerCase();
            const text = textOf(node);
            if (!text) return;

            if (tag === "h2" || tag === "h3") {
                section = node.id;
                // Drop the trailing anchor-link glyph. It is a link a reader
                // clicks, not part of what the heading says, and leaving it in
                // puts a bare # on the end of every section result.
                entries.push({
                    kind: "heading", level: tag, id: node.id, node,
                    text: text.replace(/\s*#$/, ""),
                });
            } else {
                entries.push({ kind: "block", level: "", id: section, text, node });
            }
        });
        return entries;
    }

    _main() {
        return document.querySelector("main") || document.body;
    }

    /**
     * Every block the assistant counted, in the order it counted them.
     *
     * This is what resolves a [[t17]] citation on a page whose markup carries
     * no ids, which is most of the site. Two rules make it agree with the
     * server: chrome is skipped, and a block nested inside a block already
     * counted is not counted again, because the server's regex consumed it
     * whole as part of its parent.
     */
    _buildBlockIndex() {
        const blocks = [];
        this._main().querySelectorAll(CITATION_BLOCK).forEach((node) => {
            if (node.closest(CITATION_DROP)) return;
            const last = blocks[blocks.length - 1];
            if (last && last.contains(node)) return;
            blocks.push(node);
        });
        return blocks;
    }

    // ---------------------------------------------------------------
    // Expand and collapse
    // ---------------------------------------------------------------

    expand() {
        this.panelTarget.classList.add("is-open");
        this.expanded = true;
        this.expandedAt = window.scrollY;
        this.viewportAt = Date.now();   // the keyboard is about to open
        this._pinAboveKeyboard();
    }

    collapse() {
        this.panelTarget.classList.remove("is-open");   // the typed text stays put
        this.expanded = false;
        this.cursor = -1;
    }

    /**
     * Collapse once the reader has scrolled on with the panel still open.
     *
     * Focus is deliberately NOT a reason to stay open. It used to be, on the
     * grounds that someone typing should not have the panel yanked away, but
     * focus stays in the input after every answer -- so the one dismissal a
     * reader reaches for first silently did nothing on exactly the screen they
     * most wanted out of. Scrolling the page is the reader looking somewhere
     * else; the panel should believe them and get out of the way.
     *
     * Scrolling the panel is reading, not leaving, and must never dismiss it.
     * Two things are needed for that, because they cover different cases:
     * overscroll-behavior on the panel stops a scrollable transcript chaining
     * its leftover scroll to the page, and the grace window below covers a
     * panel too short to scroll at all, whose gesture never had anywhere to go
     * but the page.
     *
     * Nor is the browser scrolling the page as the keyboard opens: see
     * KEYBOARD_GRACE.
     */
    _maybeCollapse() {
        if (!this.expanded) return;
        const outcome = scrollOutcome({
            now: Date.now(), scrollY: window.scrollY, expandedAt: this.expandedAt,
            panelScrollAt: this.panelScrollAt, viewportAt: this.viewportAt,
        });
        if (outcome === "rebase") this.expandedAt = window.scrollY;
        if (outcome === "collapse") {
            this.inputTarget.blur();
            this.collapse();
        }
    }

    /**
     * A click anywhere else is the reader addressing the page, not the bar.
     * pointerdown rather than click so the panel is gone before whatever was
     * clicked responds, and so a drag that starts outside never reopens it.
     */
    _outsideClick(event) {
        if (!this.expanded) return;
        if (this.element.contains(event.target)) return;
        this.collapse();
    }

    /**
     * Drag the panel down to dismiss it.
     *
     * Touch only. A mouse has the Close button and the whole rest of the page
     * to click, and claiming pointermove for a mouse would break selecting the
     * answer's text, which is the main thing anyone does with an answer.
     */
    _dragStart(event) {
        if (event.pointerType !== "touch" || !this.expanded) return;
        this.dragFrom = event.clientY;
        this.panelTarget.dataset.dragging = "true";
    }

    _dragMove(event) {
        if (this.dragFrom === null) return;
        // Downward only. Dragging up is not a gesture here, and letting the
        // panel follow would lift it off the bottom of the screen.
        const travelled = Math.max(0, event.clientY - this.dragFrom);
        this.panelTarget.style.transform = "translateY(" + travelled + "px)";
    }

    _dragEnd() {
        if (this.dragFrom === null) return;
        const travelled = parseFloat(
            (this.panelTarget.style.transform.match(/([\d.]+)px/) || [0, 0])[1]);
        this.dragFrom = null;
        delete this.panelTarget.dataset.dragging;
        this.panelTarget.style.transform = "";
        if (travelled > DRAG_DISMISS_PX) {
            this.inputTarget.blur();
            this.collapse();
        }
    }

    /**
     * Keep the bar above the software keyboard on a phone.
     *
     * visualViewport rather than window.innerHeight: innerHeight does not change
     * when the keyboard opens on iOS, so a bar positioned from it sits behind
     * the keyboard exactly when someone is typing into it.
     */
    _pinAboveKeyboard() {
        const viewport = window.visualViewport;
        if (!viewport) return;

        // Only while this input is the one being typed into. A keyboard opened
        // for some other field on the page is not the bar's business, and
        // riding above it would put the bar over the content instead.
        const lift = document.activeElement === this.inputTarget
            ? keyboardLift({
                innerHeight: window.innerHeight,
                viewportHeight: viewport.height,
                viewportTop: viewport.offsetTop,
            })
            : 0;
        // These events fire on every scroll frame. Writing an unchanged
        // transform is still a style write, so only write a change.
        const next = lift ? `translateY(${-lift}px)` : "";
        if (this.element.style.transform !== next) this.element.style.transform = next;
    }

    // ---------------------------------------------------------------
    // Find
    // ---------------------------------------------------------------

    find() {
        const query = this.inputTarget.value.trim().toLowerCase();
        this.element.classList.toggle("page-bar--searching", query.length > 0);
        this.cursor = -1;

        if (query.length < 2) {
            this.matches = [];
            this.resultsTarget.innerHTML = "";
            return;
        }

        // Typing is a request to see results, and the panel is not always open
        // when it arrives: activating a result collapses the panel but leaves
        // the input focused, so the next keystroke fires no focus event.
        // Without this the reader types a second query into a bar that looks
        // like it has stopped working.
        if (!this.expanded) this.expand();

        this.matches = this._search(query);
        this._render(query);
    }

    /** Headings before blocks: a section is a better answer than a sentence. */
    _search(query) {
        const hits = [];
        for (const kind of ["heading", "block"]) {
            for (const entry of this.entries) {
                if (hits.length >= MAX_RESULTS) return hits;
                if (entry.kind !== kind) continue;
                const at = entry.text.toLowerCase().indexOf(query);
                if (at !== -1) hits.push({ entry, at });
            }
        }
        return hits;
    }

    _render(query) {
        // Two different dead ends. On a page the assistant can answer for, "no
        // matches" is a step on the way to asking and the ask row sits right
        // under it. On a find-only page it is the end of the road, and saying
        // only "nothing matches" leaves the reader retyping a question at a bar
        // that has no way to tell them it cannot take one.
        const empty = '<li class="page-bar-empty">Nothing on this page matches that.'
            + (this.askUrlValue ? "" : " This page is search only.")
            + "</li>";
        const hits = this.matches.length
            ? this.matches.map((match, index) =>
                this._resultHtml(match, index, query.length)).join("")
            : empty;

        this.resultsTarget.innerHTML = hits + this._askRowHtml(query);
        Array.from(this.resultsTarget.querySelectorAll(".page-bar-result"))
            .forEach((button, index) => {
                button.addEventListener("click", () => this._activate(index));
            });
    }

    /**
     * The offer to ask, shown once the query has stopped looking like a
     * lookup. It is a row in the results list rather than a mode switch, so
     * the reader never has to decide which half of the bar they are in before
     * they have finished typing.
     */
    _askRowHtml(query) {
        // No ask URL means the server will not answer for this page, so the
        // bar is find-only and says nothing about asking.
        if (!this.askUrlValue) return "";
        // Also offered when the page has nothing to find: a reader who typed
        // something this page does not contain is exactly the reader an answer
        // helps, and "no results" on its own is a dead end.
        if (this.matches.length && !this._looksLikeQuestion(query)) return "";
        return '<li><button type="button" class="page-bar-askrow"'
            + ' data-action="click->page-bar#ask">'
            + '<span class="page-bar-askrow__lead">Ask Chip AI</span>'
            + escapeHtml(this.inputTarget.value.trim())
            + "</button></li>";
    }

    _looksLikeQuestion(query) {
        return query.trim().split(/\s+/).length > 3
            || query.trim().endsWith("?")
            || QUESTION_WORD.test(query.trim());
    }

    _resultHtml(match, index, length) {
        const { entry, at } = match;
        const indent = entry.level === "h3" ? " page-bar-result--h3" : "";
        const kind = entry.kind === "heading"
            ? '<span class="page-bar-result__kind">Section</span>' : "";
        return '<li><button type="button" class="page-bar-result' + indent + '"'
            + ' aria-selected="' + (index === this.cursor) + '">'
            + kind + snippet(entry.text, at, length)
            + "</button></li>";
    }

    // ---------------------------------------------------------------
    // Activation
    // ---------------------------------------------------------------

    /** Scroll to a result, name it in the URL, and say which one it was. */
    _activate(index) {
        const match = this.matches[index];
        if (!match) return;
        this._track("page_bar_find", { query_length: this.inputTarget.value.length });
        this._reveal(match.entry.node, match.entry.id);
        this.collapse();
    }

    /**
     * Put a node in front of the reader and mark it as the thing they asked
     * for. Shared by a find result and a citation chip, because from the
     * reader's side those are the same act.
     */
    _reveal(node, anchorId) {
        node.scrollIntoView({ block: "center" });
        if (anchorId && document.getElementById(anchorId) === node) {
            history.replaceState(null, "", "#" + anchorId);
        }
        node.classList.add("page-bar-target");
        window.setTimeout(() => node.classList.remove("page-bar-target"), HIGHLIGHT_MS);
    }

    _moveCursor(step) {
        if (!this.matches.length) return;
        const count = this.matches.length;
        this.cursor = (this.cursor + step + count) % count;
        Array.from(this.resultsTarget.querySelectorAll(".page-bar-result"))
            .forEach((button, index) => {
                button.setAttribute("aria-selected", String(index === this.cursor));
            });
    }

    // ---------------------------------------------------------------
    // Keyboard
    // ---------------------------------------------------------------

    keydown(event) {
        const handlers = {
            ArrowDown: () => this._moveCursor(1),
            ArrowUp: () => this._moveCursor(-1),
            Escape: () => { this.collapse(); this.inputTarget.blur(); },
            Enter: () => this._enter(),
        };
        const handler = handlers[event.key];
        if (!handler) return;
        event.preventDefault();
        handler();
    }

    /**
     * "/" focuses the bar from anywhere on the page, the way it does in every
     * developer tool and documentation site the reader already uses.
     */
    _globalKey(event) {
        if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
        const target = event.target;
        const tag = target && target.tagName ? target.tagName.toLowerCase() : "";
        if (tag === "input" || tag === "textarea" || (target && target.isContentEditable)) return;
        event.preventDefault();
        this.inputTarget.focus();
    }

    /**
     * Enter means "the obvious thing", and which thing that is depends on
     * what the reader has done. Arrowed onto a result: go there. Otherwise a
     * question goes to the assistant and a lookup goes to the first hit, so
     * neither kind of reader has to aim.
     */
    _enter() {
        if (this.cursor !== -1) return this._activate(this.cursor);
        const query = this.inputTarget.value.trim();
        // Enter is never allowed to do nothing. _activate on an empty result
        // set returns silently, so on a find-only page with no matches the key
        // simply stopped responding and the bar looked broken. Showing the
        // panel at least puts the reason on screen.
        if (!this.askUrlValue) {
            return this.matches.length ? this._activate(0) : this.expand();
        }
        if (this.matches.length && !this._looksLikeQuestion(query)) {
            return this._activate(0);
        }
        return this.ask();
    }

    submit(event) {
        event.preventDefault();
        this._enter();
    }

    // ---------------------------------------------------------------
    // Ask
    // ---------------------------------------------------------------

    askStarter(event) {
        this.inputTarget.value = textOf(event.currentTarget);
        this.ask();
    }

    /** Clear the conversation but leave the page as the reader left it. */
    _resetConversation() {
        this.asking = false;
        this.rawText = "";
        this.grounding = null;
        this.citations = [];
        this.uiMessages = [];
        this.historyBlob = "";
        this.controller = null;
        this.firstTokenTimer = null;
        this.lastQuestion = "";
        this.answeredQuestion = null;
    }

    reset() {
        this._resetConversation();
        this.askTarget.hidden = true;
        this.questionTarget.hidden = true;
        this.questionTarget.textContent = "";
        this.askedByTarget.hidden = true;
        this.answeredByTarget.hidden = true;
        this.disclosureTarget.hidden = true;
        this.cautionTarget.hidden = true;
        this.pastTarget.hidden = true;
        this.pastTarget.innerHTML = "";
        this.inputTarget.value = "";
        this.find();
        this.inputTarget.focus();
    }

    async ask() {
        const question = this.inputTarget.value.trim();
        if (!question || this.asking || !this.askUrlValue) return;

        this.asking = true;
        this.lastQuestion = question;
        // The question is about to be rendered above the answer, so leaving it
        // in the box says it twice -- and a second Enter would silently re-ask
        // it. find() clears the result list that was matching the old text.
        this.inputTarget.value = "";
        this.find();
        this.uiMessages.push({ role: "user", content: question });
        // Six turns of context, matching what the panel keeps on screen. The
        // server caps the session too; this keeps the request from growing
        // past what the reader can still see and reason about.
        if (this.uiMessages.length > MAX_TURNS * 2) {
            this.uiMessages = this.uiMessages.slice(-MAX_TURNS * 2);
        }
        this._openAnswer();

        try {
            await this._stream(question);
        } catch (error) {
            if (error && error.name !== "AbortError") this._failed("network");
        } finally {
            this._closeAnswer();
        }
    }

    /**
     * Reserve the answer's height before the network call, not after it.
     *
     * The skeleton is three lines tall because a short answer is about three
     * lines. Inserting an empty box now and growing it when text arrives is
     * the same layout shift, just later and inside the panel the reader is
     * looking at.
     */
    _openAnswer() {
        this.expand();
        this._archive();
        this.rawText = "";
        this.grounding = null;
        this.citations = [];
        this.questionTarget.textContent = this.lastQuestion;
        this.questionTarget.hidden = false;
        this.askedByTarget.hidden = false;
        this.answeredByTarget.hidden = false;
        this.askTarget.hidden = false;
        this.askTarget.dataset.grounding = "";
        this.sourcesTarget.hidden = true;
        this.elsewhereTarget.hidden = true;
        this.afterTarget.hidden = true;
        this.fallbackTarget.hidden = true;
        this.thanksTarget.hidden = true;
        this.disclosureTarget.hidden = false;
        // Once per session, on the answer someone is actually reading. Every
        // answer after it has the same caveats and a reader who has seen them
        // once stops seeing them at all.
        this.cautionTarget.hidden = this.pastTarget.children.length > 0;
        this.chipsTarget.innerHTML = "";
        this.answerTarget.innerHTML =
            '<div class="page-bar-typing" role="status" aria-label="Chip AI is typing">'
            + "<span></span><span></span><span></span></div>";
    }

    /**
     * Move the answer that is on screen up into the transcript, so a follow-up
     * question does not erase what it is following up on. Only answers that
     * arrived are kept: a failure message re-shown above the retry would read
     * as two separate things going wrong.
     */
    /**
     * A speaker label for an archived turn, copied from the live one so both
     * names are written once, in the template, where a writer can find them.
     */
    _speaker(source) {
        const who = document.createElement("p");
        who.className = "page-bar-speaker";
        who.textContent = source.textContent;
        return who;
    }

    _archive() {
        if (!this.rawText || !this.answeredQuestion) return;

        const turn = document.createElement("div");
        turn.className = "page-bar-turn";

        turn.appendChild(this._speaker(this.askedByTarget));
        const asked = document.createElement("p");
        asked.className = "page-bar-msg page-bar-msg--you";
        asked.textContent = this.answeredQuestion;
        turn.appendChild(asked);

        turn.appendChild(this._speaker(this.answeredByTarget));
        const answered = document.createElement("div");
        answered.className = "page-bar-msg page-bar-msg--ai";
        answered.innerHTML = this.answerTarget.innerHTML;
        turn.appendChild(answered);

        this.pastTarget.appendChild(turn);
        this.pastTarget.hidden = false;
        while (this.pastTarget.children.length > MAX_TURNS - 1) {
            this.pastTarget.removeChild(this.pastTarget.firstElementChild);
        }
    }

    _closeAnswer() {
        this.asking = false;
        this.answeredQuestion = this.rawText ? this.lastQuestion : null;
        this._clearFirstTokenTimer();
        this.controller = null;
        if (!this.rawText) {
            // A stream that closed with nothing in it. The reader is looking
            // at a skeleton that will never fill, so say so rather than leave
            // it pulsing. A failure has already put its own message up.
            if (this.askTarget.dataset.grounding !== "failed") this._failed("empty");
            return;
        }

        this.uiMessages.push({ role: "assistant", content: this.rawText });
        this.afterTarget.hidden = false;
        this.fallbackTarget.hidden = !(this.grounding === "none"
            || this.grounding === "related");
        this._renderCitations();
        this._track("page_bar_ask", {
            page: this.pathValue,
            grounding: this.grounding || "unshaped",
        });
    }

    async _stream(question) {
        this.controller = new AbortController();
        this._startFirstTokenTimer();

        const response = await fetch(this.askUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
            body: JSON.stringify({
                messages: this.uiMessages,
                history: this.historyBlob,
                page_path: this.pathValue,
            }),
            signal: this.controller.signal,
        });

        if (response.status === 429) return this._failed("rate");
        if (!response.ok || !response.body) return this._failed("server");
        return this._consume(response);
    }

    /**
     * SSE frames: blank-line separated, `event:` and `data:` fields, JSON
     * payloads. Same wire handling as public_chat_controller.js, copied rather
     * than extracted: two call sites is one short of a shared module, and the
     * shape is forty lines that have not changed since the widget shipped.
     */
    async _consume(response) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        for (;;) {
            const chunk = await reader.read();
            if (chunk.done) break;
            buffer += decoder.decode(chunk.value, { stream: true });

            let split = buffer.indexOf("\n\n");
            while (split !== -1) {
                this._frame(buffer.slice(0, split));
                buffer = buffer.slice(split + 2);
                split = buffer.indexOf("\n\n");
            }
        }
    }

    _frame(frame) {
        let name = "message";
        const data = [];
        frame.split("\n").forEach((line) => {
            if (line.indexOf("event:") === 0) name = line.slice(6).trim();
            else if (line.indexOf("data:") === 0) data.push(line.slice(5).trim());
        });
        if (!data.length) return;

        let payload;
        try {
            payload = JSON.parse(data.join("\n"));
        } catch (_error) {
            return;
        }
        this._handle(name, payload);
    }

    _handle(name, payload) {
        if (name === "text_delta") {
            this._clearFirstTokenTimer();
            this.rawText += payload.chunk || "";
            this._renderAnswer();
        } else if (name === "done") {
            this.historyBlob = payload.history || this.historyBlob;
            this._renderPages(payload.pages || []);
        } else if (name === "error") {
            // The server's own message is written for the floating widget and
            // points at a FAQ link this surface does not render. The daily cap
            // is the same event; what a reader should do about it is not.
            const capped = payload.code === "capped" || payload.code === "session_cap";
            this._failed(capped ? "capped" : "server",
                         capped ? null : payload.message);
        }
    }

    // ---------------------------------------------------------------
    // Rendering the answer
    // ---------------------------------------------------------------

    /**
     * Split the reply into its grounding and the part a reader should see.
     *
     * The <check> line is machinery and must never flash on screen, which it
     * would if the first chunk arrived as "<check" and got rendered. Anything
     * that opens with "<" and has not reached a newline yet is still that
     * line, so it renders as nothing.
     */
    _split() {
        const match = CHECK_LINE.exec(this.rawText);
        if (match) {
            return {
                grounding: match[1].toLowerCase(),
                body: this.rawText.slice(match[0].length).replace(/^\s+/, ""),
            };
        }
        if (this.rawText.trimStart().startsWith("<") && !this.rawText.includes("\n")) {
            return { grounding: null, body: "" };
        }
        return { grounding: null, body: this.rawText };
    }

    _renderAnswer() {
        const { grounding, body } = this._split();
        this.grounding = grounding;
        this.askTarget.dataset.grounding = grounding || "";

        this.citations = [];
        const visible = body
            .replace(PAGE_REFERENCE, "$1")
            .replace(CITATION, (match, id) => {
                if (!this.citations.includes(id)) this.citations.push(id);
                return "";
            });
        this.answerTarget.innerHTML = renderMarkdown(visible.replace(/[ \t]{2,}/g, " "));
        this._followStream();
    }

    /**
     * Keep the newest text in view as it arrives, but only while the reader is
     * already at the bottom. Someone who has scrolled up to re-read the start
     * of an answer is reading it, and yanking them back down is the panel
     * deciding it knows better.
     */
    _followStream() {
        const panel = this.panelTarget;
        const distance = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
        if (distance < 60) panel.scrollTop = panel.scrollHeight;
    }

    /**
     * One card per page the answer pointed at.
     *
     * A real link, not a scroll: these leave the page, which is the opposite of
     * what a source chip does, so they are shaped and labelled differently and
     * kept in their own region. The server has already checked each path
     * against the page graph, so nothing here can 404.
     */
    _renderPages(pages) {
        this.elsewhereTarget.hidden = pages.length === 0;
        this.cardsTarget.innerHTML = "";
        pages.forEach((page) => {
            const card = document.createElement("a");
            card.className = "page-bar-card";
            card.href = page.path;

            const title = document.createElement("span");
            title.className = "page-bar-card__title";
            title.textContent = page.title;
            card.appendChild(title);

            const blurb = document.createElement("span");
            blurb.className = "page-bar-card__blurb";
            blurb.textContent = page.blurb;
            card.appendChild(blurb);

            card.addEventListener("click", () => {
                this._track("page_bar_page_click", {
                    page: this.pathValue,
                    to: page.path,
                });
            });
            this.cardsTarget.appendChild(card);
        });
    }

    /**
     * One chip per passage the answer cited, skipping any the page cannot
     * actually show. A citation that scrolls nowhere is worse than no
     * citation: it reads as a broken feature rather than a missing one.
     */
    _renderCitations() {
        const chips = this.citations
            .map((id) => ({ id, node: this._citedNode(id) }))
            .filter((chip) => chip.node)
            .slice(0, MAX_CITATIONS);

        this.sourcesTarget.hidden = chips.length === 0;
        this.chipsTarget.innerHTML = "";
        chips.forEach((chip) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "page-bar-chip";
            button.textContent = this._citedLabel(chip.node);
            button.addEventListener("click", () => {
                this._track("page_bar_cite_click", { page: this.pathValue });
                this._reveal(chip.node, chip.id);
                this.collapse();
            });
            this.chipsTarget.appendChild(button);
        });
    }

    /** The element a citation id names, or null if the page has no such thing. */
    _citedNode(id) {
        const positional = /^t(\d+)$/.exec(id);
        if (positional) return this.blocks[Number(positional[1]) - 1] || null;
        return this._main().querySelector('[data-block="' + id + '"]')
            || document.getElementById(id);
    }

    /** A cited passage is named by the section it sits in, not by its own text. */
    _citedLabel(node) {
        let label = "";
        for (const entry of this.entries) {
            if (entry.kind !== "heading") continue;
            if (entry.node === node) return entry.text;
            const after = entry.node.compareDocumentPosition(node)
                & Node.DOCUMENT_POSITION_FOLLOWING;
            if (after) label = entry.text;
        }
        return label || textOf(node).slice(0, 40);
    }

    // ---------------------------------------------------------------
    // Failure
    // ---------------------------------------------------------------

    /**
     * Every failure keeps find working and says so. The bar is two tools in
     * one strip, and the half that needs no network is the half that still
     * works when the other one cannot.
     */
    _failed(kind, message) {
        this._abort();
        const copy = {
            capped: "That's the limit of what I can answer today. The find box "
                + "still works, and the pricing page covers plans.",
            rate: "That's a lot of questions at once. Give it a minute, the "
                + "find box still works.",
            network: "Couldn't reach the assistant. Try again, or use find.",
            server: message || "That one did not come back. Try again, or use find.",
            empty: "Nothing came back for that one. Try asking it a different "
                + "way, or use find.",
        }[kind] || "Something went wrong. The find box still works.";

        const retry = kind !== "capped" && kind !== "rate"
            ? '<button type="button" class="page-bar-retry"'
              + ' data-action="click->page-bar#ask">Try again</button>'
            : "";
        this.askTarget.dataset.grounding = "failed";
        this.answerTarget.innerHTML =
            '<p class="page-bar-failed">' + escapeHtml(copy) + "</p>" + retry;
        // The turn never happened, so it must not sit in the history the next
        // question carries: the model would see a question it never answered,
        // and a half-arrived answer is worse context than none.
        this.uiMessages.pop();
        this.rawText = "";
    }

    _startFirstTokenTimer() {
        this.firstTokenTimer = window.setTimeout(() => {
            if (!this.rawText) this._failed("network");
        }, FIRST_TOKEN_MS);
    }

    _clearFirstTokenTimer() {
        if (this.firstTokenTimer === null) return;
        window.clearTimeout(this.firstTokenTimer);
        this.firstTokenTimer = null;
    }

    _abort() {
        this._clearFirstTokenTimer();
        if (this.controller) this.controller.abort();
        this.controller = null;
    }

    // ---------------------------------------------------------------
    // Feedback and analytics
    // ---------------------------------------------------------------

    vote(event) {
        this._track("page_bar_feedback", {
            page: this.pathValue,
            vote: event.currentTarget.dataset.vote,
            grounding: this.grounding || "unshaped",
        });
        this.thanksTarget.hidden = false;
        Array.from(this.afterTarget.querySelectorAll(".page-bar-vote__btn"))
            .forEach((button) => { button.disabled = true; });
    }

    /**
     * Never the question text. It is already in public_chat_turns, where it is
     * covered by the retention policy that surface has; a copy in a third-party
     * analytics store would not be.
     */
    _track(name, properties) {
        if (window.posthog && typeof window.posthog.capture === "function") {
            window.posthog.capture(name, properties);
        }
    }
}
